'use client'

import { useMemo, useState } from 'react'
import { Check, Copy, Play } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  MINIMAX_H3_CATEGORY_LABELS,
  MINIMAX_H3_CDN,
  MINIMAX_H3_MODE_LABELS,
  MINIMAX_H3_PROMPTS,
  type MinimaxH3AspectRatio,
  type MinimaxH3Category,
  type MinimaxH3Mode,
  type MinimaxH3Prompt,
} from '@/content/marketing/minimax-h3-prompts/prompts'
import { track } from '@/lib/analytics/track'

const ASPECT_CLASS: Record<MinimaxH3AspectRatio, string> = {
  '16:9': 'aspect-video',
  '9:16': 'aspect-[9/16]',
  '1:1': 'aspect-square',
  '4:3': 'aspect-[4/3]',
  '21:9': 'aspect-[21/9]',
}

const MODE_ORDER: MinimaxH3Mode[] = ['edit', 'reference', 'generate']

/** Categories in the order they appear in the data, so the filter row is stable. */
const CATEGORIES = MINIMAX_H3_PROMPTS.reduce<MinimaxH3Category[]>((acc, p) => {
  if (!acc.includes(p.category)) acc.push(p.category)
  return acc
}, [])

function CopyButton({ prompt }: { prompt: MinimaxH3Prompt }) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(prompt.prompt)
      setCopied(true)
      track('minimax_h3_prompt_copy', {
        slug: prompt.slug,
        category: prompt.category,
        mode: prompt.mode,
      })
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      // Clipboard blocked — the prompt is on screen either way.
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground/80 transition-colors hover:border-foreground/30 hover:text-foreground"
      aria-label={copied ? 'Prompt copied' : 'Copy prompt'}
    >
      {copied ? (
        <>
          <Check className="h-4 w-4" />
          Copied
        </>
      ) : (
        <>
          <Copy className="h-4 w-4" />
          Copy prompt
        </>
      )}
    </button>
  )
}

function PromptCard({ prompt }: { prompt: MinimaxH3Prompt }) {
  const [playing, setPlaying] = useState(false)

  return (
    <article className="mb-6 flex break-inside-avoid flex-col overflow-hidden rounded-2xl border border-border bg-background transition-shadow hover:shadow-lg">
      {prompt.hasSample && (
        <div
          className={`relative w-full overflow-hidden bg-black ${ASPECT_CLASS[prompt.aspectRatio]}`}
        >
          <video
            src={`${MINIMAX_H3_CDN}/${prompt.slug}.mp4`}
            poster={`${MINIMAX_H3_CDN}/${prompt.slug}.jpg`}
            muted
            loop
            playsInline
            preload="metadata"
            className="h-full w-full object-cover"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onClick={e => {
              const el = e.currentTarget
              if (el.paused) {
                void el.play().catch(() => {})
                track('minimax_h3_sample_play', { slug: prompt.slug })
              } else {
                el.pause()
              }
            }}
          />
          {!playing && (
            <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/15">
              <Play className="size-9 text-white/85" />
            </span>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="secondary" className="rounded-full text-[10px]">
            {MINIMAX_H3_CATEGORY_LABELS[prompt.category]}
          </Badge>
          <Badge variant="outline" className="rounded-full text-[10px]">
            {MINIMAX_H3_MODE_LABELS[prompt.mode]}
          </Badge>
        </div>

        <div>
          <h3 className="text-base font-semibold text-foreground">
            {prompt.title}
          </h3>
          <p className="mt-1.5 text-xs text-muted-foreground">{prompt.useCase}</p>
        </div>

        <p className="text-sm leading-relaxed text-foreground/75">
          {prompt.prompt}
        </p>

        <div className="mt-auto flex flex-col gap-3 pt-1">
          <p className="text-xs text-muted-foreground">
            <span className="font-medium text-foreground/70">Inputs:</span>{' '}
            {prompt.inputs}
          </p>
          <CopyButton prompt={prompt} />
        </div>
      </div>
    </article>
  )
}

/**
 * @param limit  Render only the first N matches. The model page uses this to
 *   show a preview and send the reader to /prompts/minimax-h3 for the rest —
 *   two URLs carrying the identical prompt grid would compete for the same
 *   "minimax h3 prompts" query.
 */
export function MinimaxH3PromptLibrary({ limit }: { limit?: number } = {}) {
  const [mode, setMode] = useState<MinimaxH3Mode | 'all'>('all')
  const [category, setCategory] = useState<MinimaxH3Category | 'all'>('all')

  const filtered = useMemo(() => {
    const matches = MINIMAX_H3_PROMPTS.filter(
      p =>
        (mode === 'all' || p.mode === mode) &&
        (category === 'all' || p.category === category),
    )
    return limit ? matches.slice(0, limit) : matches
  }, [mode, category, limit])

  /** Only offer categories that survive the current mode filter. */
  const visibleCategories = useMemo(
    () =>
      CATEGORIES.filter(c =>
        MINIMAX_H3_PROMPTS.some(
          p => p.category === c && (mode === 'all' || p.mode === mode),
        ),
      ),
    [mode],
  )

  return (
    <div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {(['all', ...MODE_ORDER] as const).map(m => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setMode(m)
                setCategory('all')
                track('minimax_h3_prompt_filter', { filter: 'mode', value: m })
              }}
              className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                mode === m
                  ? 'border-primary bg-primary/10 text-foreground'
                  : 'border-border text-muted-foreground hover:border-foreground/30 hover:text-foreground'
              }`}
            >
              {m === 'all'
                ? `All ${MINIMAX_H3_PROMPTS.length}`
                : MINIMAX_H3_MODE_LABELS[m]}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {(['all', ...visibleCategories] as const).map(c => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setCategory(c)
                track('minimax_h3_prompt_filter', {
                  filter: 'category',
                  value: c,
                })
              }}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                category === c
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {c === 'all' ? 'Every category' : MINIMAX_H3_CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-8 columns-1 gap-6 sm:columns-2 lg:columns-3">
        {filtered.map(prompt => (
          <PromptCard key={prompt.slug} prompt={prompt} />
        ))}
      </div>

      {filtered.length === 0 && (
        <p className="mt-8 text-sm text-muted-foreground">
          No prompts match that combination yet.
        </p>
      )}

      <div className="mt-4 flex flex-wrap justify-center gap-3">
        {limit && (
          <Button variant="ghost" className="rounded-full" asChild>
            <a href="/prompts/minimax-h3">
              See all {MINIMAX_H3_PROMPTS.length} H3 prompts
            </a>
          </Button>
        )}
        <Button variant="outline" className="rounded-full" asChild>
          <a href="/sign-up" target="_blank" rel="noopener noreferrer">
            Run these prompts in Soku AI
          </a>
        </Button>
      </div>
    </div>
  )
}
